import { useMemo } from 'react'
import { ArrowLeft, CheckSquare, FileText, FolderOpen } from 'lucide-react'
import type { LinkableEntityType } from '@/lib/constants/entityTypes'
import type { EnrichedEntityLink } from '@/lib/db/rxdb/hooks/entities/entity-links'
import { useBacklinks } from '@/lib/db/rxdb/hooks'
import { ENTITY_TYPES, LINKABLE_ENTITY_LABELS } from '@/lib/constants/entityTypes'
import { ProjectIcon } from '@/lib/utils/projectIcon'

interface BacklinksSectionProps {
  entityId: string
  onLinkClick?: (entityId: string, entityType: string) => void
}

const ENTITY_TYPE_ICONS: Record<string, typeof FileText> = {
  [ENTITY_TYPES.WORK_ITEM]: CheckSquare,
  [ENTITY_TYPES.DOCUMENT]: FileText,
  [ENTITY_TYPES.PROJECT]: FolderOpen,
}

function getLinkableEntityLabel(entityType: string) {
  if (entityType in LINKABLE_ENTITY_LABELS) {
    return LINKABLE_ENTITY_LABELS[entityType as LinkableEntityType]
  }

  return entityType
}

function BacklinkEntityIcon({ link }: { link: EnrichedEntityLink }) {
  if (!link.resolvedSourceIcon) return null

  if (link.sourceEntityType === ENTITY_TYPES.WORK_ITEM) {
    return (
      <span className="text-muted-foreground mr-1.5 text-xs">
        {link.resolvedSourceIcon}
      </span>
    )
  }

  if (
    link.sourceEntityType === ENTITY_TYPES.DOCUMENT ||
    link.sourceEntityType === ENTITY_TYPES.PROJECT
  ) {
    return (
      <ProjectIcon
        iconType={link.resolvedSourceIconType}
        icon={link.resolvedSourceIcon}
        iconColor={link.resolvedSourceIconColor}
        className="inline mr-1.5 -mt-0.5 text-muted-foreground"
        size={14}
      />
    )
  }

  return null
}

export function BacklinksSection({
  entityId,
  onLinkClick,
}: BacklinksSectionProps) {
  const { backlinks, isLoading } = useBacklinks(entityId)

  // Group backlinks by source entity type
  const groupedBacklinks = useMemo(() => {
    const groups = new Map<string, typeof backlinks>()
    for (const link of backlinks) {
      const label = getLinkableEntityLabel(link.sourceEntityType)
      const group = groups.get(label)
      if (group) {
        group.push(link)
      } else {
        groups.set(label, [link])
      }
    }
    return groups
  }, [backlinks])

  if (isLoading || backlinks.length === 0) return null

  return (
    <div>
      <h3 className="text-sm font-medium flex items-center gap-1.5">
        <ArrowLeft className="size-4" />
        Referenced by
        <span className="text-muted-foreground">({backlinks.length})</span>
      </h3>

      <div className="pt-2">
        <div className="space-y-2">
          {Array.from(groupedBacklinks.entries()).map(([label, groupLinks]) => (
            <div key={label} className="space-y-0.5">
              <p className="text-xs font-medium text-muted-foreground px-2 uppercase tracking-wide">
                {label}
              </p>
              {groupLinks.map((link) => {
                const Icon = ENTITY_TYPE_ICONS[link.sourceEntityType] ?? FileText
                return (
                  <div
                    key={link.id}
                    className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent"
                  >
                    <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                    <button
                      type="button"
                      className="flex-1 text-left text-sm truncate cursor-pointer"
                      onClick={() =>
                        onLinkClick?.(link.sourceEntityId, link.sourceEntityType)
                      }
                    >
                      <BacklinkEntityIcon link={link} />
                      {link.resolvedSourceName || 'Untitled'}
                    </button>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
